/**
 * Data-Sync Run History (#132) — client wrapper for the staff cross-org read.
 *
 * One call returns every data-sync run across all practices/clinics in the
 * window, plus fleet-level KPIs. Staff-only; the backend re-checks the
 * allowlist. v1 manual-refresh (no live subscription) per the PRD.
 */

import { httpsCallable } from 'firebase/functions'
import { functions } from '../config'

export type SyncPool = 'revolution' | 'vsp' | 'eyemed' | 'versant' | 'csv'
export type SyncStatus = 'queued' | 'running' | 'completed' | 'errored'
export type SyncWindow = '24h' | '7d' | '30d'

export interface DataSyncRun {
  id: string
  orgId: string
  practiceName: string
  clinicId: string
  clinicName: string
  pool: SyncPool
  status: SyncStatus
  attempt: number
  initiatedAt: string
  startedAt: string | null
  endedAt: string | null
  queueWaitMs: number | null
  runDurationMs: number | null
  patientCount: number | null
  timePerPatientMs: number | null
  targetDate: string | null
  error: string | null
  errorHistory: { error: string; timestamp: string }[]
  /** The retired engine's job id on a run it performed — text, no link; null on lane runs. */
  uipathJobId: string | null
  /** The retired engine's machine on a run it performed; null on lane runs. */
  uipathMachine: string | null
  /** The dispatch record — the lane unit, or the retired engine's `in_*` dict; null when not recorded. */
  uipathJobInputs: Record<string, string> | null
  /** Short Cloud Run execution id for a lane-dispatched run; null on a run the retired engine
   *  performed, and on a lane run not yet dispatched. */
  gcpExecution: string | null
  /** Cloud Logging deep link filtered to this run's log lines; null off the gcp lane. */
  gcpLogsUrl: string | null
  /** Cloud Storage console link to this run's artifacts folder (trace, failure screenshot/DOM); null off the gcp lane. */
  gcpArtifactsUrl: string | null
  /** True when an errored run is shown as completed via a staff success-override. */
  overridden: boolean
  /** Who/why/when of the staff success-override; present only when `overridden`. */
  successOverride: { actorEmail: string; reason: string; createdAt: string } | null
}

export interface DataSyncSummary {
  totalRuns: number
  failed: number
  running: number
  queued: number
  completed: number
  successRatePct: number | null
  timePerPatientMs: Partial<Record<SyncPool, number>>
  oldestQueuedMs: number | null
}

export interface DataSyncRunsResult {
  window: SyncWindow
  generatedAt: string
  practiceCount: number
  clinicCount: number
  capped: boolean
  /** Sources whose oldest rows were dropped by the per-source row cap. */
  cappedSources: SyncPool[]
  summary: DataSyncSummary
  runs: DataSyncRun[]
}

/** Fetch the cross-org run history for the given window (default 24h). */
export async function listDataSyncRuns(window: SyncWindow = '24h'): Promise<DataSyncRunsResult> {
  const fn = httpsCallable<{ window: SyncWindow }, DataSyncRunsResult>(
    functions,
    'listDataSyncRunsForStaff',
  )
  const res = await fn({ window })
  return res.data
}

/**
 * Staff-only: mark an errored run as "really succeeded" (`override = true`) or
 * remove a prior override (`override = false`). A `reason` (min 3 chars) is
 * required when setting. Non-destructive — only the staff fleet view + its KPIs
 * reflect it; the customer dashboard is unchanged.
 */
export async function setRunSuccessOverride(
  run: Pick<DataSyncRun, 'orgId' | 'id' | 'pool'>,
  override: boolean,
  reason?: string,
): Promise<void> {
  const fn = httpsCallable<
    { orgId: string; runId: string; pool: SyncPool; override: boolean; reason?: string },
    { ok: true; overridden: boolean }
  >(functions, 'setDataSyncRunSuccessOverride')
  await fn({ orgId: run.orgId, runId: run.id, pool: run.pool, override, ...(reason ? { reason } : {}) })
}

/** Pools that map to a re-runnable bot. A `csv` row is an upload, not a run. */
export type RerunPool = Exclude<SyncPool, 'csv'>

export interface RerunUnitResult {
  action: string
  reason?: string
  runId?: string | null
  checkId?: string
}

export interface RerunResult {
  pool: RerunPool
  /**
   * Present INSTEAD of results — nothing ran, re-call to go ahead:
   *   - `confirm_past` (Revolution): the target day is past and a forced
   *     re-pull rewrites it. Re-call with `confirmPast`.
   *   - `reused` (VSP/EyeMed): a recent check already answers this, so no bot
   *     ran. Re-call with `force` to bypass the cache and buy a fresh answer.
   */
  needsConfirm?: 'confirm_past' | 'reused'
  appointmentDate: string | null
  results: RerunUnitResult[]
}

export function isRerunnable(pool: SyncPool): pool is RerunPool {
  return pool !== 'csv'
}

/**
 * Staff-only: re-fire the bot behind a run. Revolution re-pulls the clinic-day
 * (a completed run re-pulls too, which is the point: repairing a night that
 * finished with odd data); VSP/EyeMed mint a fresh check in the source's flow
 * and retire the source.
 *
 * Both pools answer a first click with `needsConfirm` rather than executing
 * when the action would be destructive (`confirm_past`) or would silently do
 * nothing (`reused`); re-call with the matching flag to go ahead.
 *
 * Only ids go over the wire — the backend derives the clinic, date, and patient
 * identity from the doc itself.
 */
export async function rerunDataSyncRun(
  run: Pick<DataSyncRun, 'orgId' | 'id'> & { pool: RerunPool },
  opts: { confirmPast?: boolean; force?: boolean } = {},
): Promise<RerunResult> {
  const fn = httpsCallable<
    {
      orgId: string; runId: string; pool: RerunPool
      confirmPast?: boolean; force?: boolean
    },
    RerunResult
  >(functions, 'rerunDataSyncRunForStaff')
  const res = await fn({
    orgId: run.orgId, runId: run.id, pool: run.pool,
    ...(opts.confirmPast ? { confirmPast: true } : {}),
    ...(opts.force ? { force: true } : {}),
  })
  return res.data
}
